/**
 * Iter60.p43 — Compact "Early Departure Note" dialog.
 *
 * Opens from either the row's 📝 chip (edit-existing) OR the three-dot
 * row menu → "Add early-departure note…" (add-new). Fields:
 *   • Reason  — Select (medical / family / school / other)
 *   • Note    — Textarea (max 500)
 *   • Excused — Checkbox (wires to `excused_early_departure`; payroll
 *              already reads this field to skip the early-departure
 *              deduction — see `payroll.py:excused_days`)
 *
 * Server writes:  PATCH /api/attendance/daily/{id}/early-departure
 * Payload shape:  { note, reason_code, excused }
 *
 * Kept intentionally small (parity with `SplitByProjectsDialog`); the
 * larger multi-field Edit-attendance modal is untouched.
 */
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { StickyNote } from "lucide-react";
import ModuleIdChip from "./ModuleIdChip";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const REASON_OPTIONS = [
    { value: "medical",  label: "Medical" },
    { value: "family",   label: "Family emergency" },
    { value: "school",   label: "School pickup" },
    { value: "other",    label: "Other" },
];

// eslint-disable-next-line react/no-unused-prop-types
export default function EarlyDepartureNoteDialog({ open, row, canEdit = true, onClose, onSaved }) {
    const [note, setNote] = useState("");
    const [reason, setReason] = useState("other");
    const [excused, setExcused] = useState(false);
    const [busy, setBusy] = useState(false);

    // Seed the form whenever a new row is selected.
    useEffect(() => {
        if (!row) return;
        setNote(row.early_departure_note || "");
        setReason(row.early_departure_reason_code || "other");
        setExcused(!!row.excused_early_departure);
    }, [row]);

    if (!row) return null;
    // Iter60.p43.1 — Read-only mode: roles without `attendance.write_manual`
    // (Finance Viewer, Read-Only Viewer, Senior Management) still open the
    // dialog to READ the note, but every input is disabled and the Save
    // button is HIDDEN. The chip is the only way to reach the note text
    // for these users; blocking the click outright (Option B) would hide
    // the very information they need. Backend already returns 403 for
    // these roles — this UI guard just makes intent obvious.
    const readOnly = !canEdit;
    const disabled = readOnly || !!row.synthetic || !row.id;

    const save = async () => {
        if (disabled) return;
        setBusy(true);
        try {
            const { data } = await api.patch(
                `/attendance/daily/${row.id}/early-departure`,
                {
                    note: (note || "").trim() || null,
                    reason_code: reason,
                    excused,
                },
            );
            toast.success((note || "").trim() ? "Early-departure note saved." : "Note cleared.");
            onSaved && onSaved(data);
            onClose && onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="early-departure-note-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 asl-navy-text">
                        <StickyNote className="h-5 w-5 text-[color:var(--asl-crimson)]" />
                        Early-departure note
                    </DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] flex flex-wrap items-center gap-x-2 gap-y-1">
                        <ModuleIdChip
                            value={row.module_employee_id}
                            testid="early-departure-note-module-id"
                        />
                        <span>{row.work_date}</span>
                        {row.first_clock_in && row.last_clock_out ? (
                            <>
                                <span>·</span>
                                <span>Out at <span className="mono">
                                    {(row.last_clock_out || "").slice(11, 16)}
                                </span></span>
                            </>
                        ) : null}
                    </div>
                    {/* Iter60.p43.1 — Read-only hint. Rendered under the
                        subtitle so the reader immediately understands why
                        the fields are inert. */}
                    {readOnly && (
                        <div
                            className="text-[11px] italic text-[color:var(--asl-muted)] mt-1"
                            data-testid="edn-read-only-hint"
                        >
                            View only — you don't have permission to edit.
                        </div>
                    )}
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="edn-reason">Reason</Label>
                        <Select value={reason} onValueChange={setReason} disabled={disabled}>
                            <SelectTrigger id="edn-reason" data-testid="edn-reason-trigger" className="w-full">
                                <SelectValue placeholder="Choose reason" />
                            </SelectTrigger>
                            <SelectContent>
                                {REASON_OPTIONS.map((r) => (
                                    <SelectItem key={r.value} value={r.value} data-testid={`edn-reason-${r.value}`}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="edn-note">Note <span className="text-[color:var(--asl-muted)] font-normal">(max 500)</span></Label>
                        <Textarea
                            id="edn-note"
                            value={note}
                            onChange={(e) => setNote(e.target.value.slice(0, 500))}
                            placeholder="Family emergency — will return tomorrow. Left at 14:15."
                            className="min-h-[96px]"
                            data-testid="edn-note-input"
                            disabled={disabled}
                        />
                        <div className="text-[10px] text-[color:var(--asl-muted)] text-right mono">
                            {note.length}/500
                        </div>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <Checkbox
                            checked={excused}
                            onCheckedChange={(v) => setExcused(!!v)}
                            data-testid="edn-excused-checkbox"
                            disabled={disabled}
                        />
                        <span className="text-sm">
                            Mark as <strong>excused</strong>
                            <span className="text-[11px] text-[color:var(--asl-muted)] ml-2">
                                Payroll skips the early-departure deduction for this day
                            </span>
                        </span>
                    </label>
                </div>

                <DialogFooter className="mt-2 gap-2">
                    <Button type="button" variant="outline" onClick={() => onClose && onClose()} data-testid="edn-cancel">
                        {readOnly ? "Close" : "Cancel"}
                    </Button>
                    {!readOnly && (
                        <Button
                            type="button"
                            className="asl-navy-bg text-white"
                            disabled={busy || disabled}
                            onClick={save}
                            data-testid="edn-save"
                        >
                            {busy ? "Saving…" : "Save"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
